// src/services/characterService.ts
import axios from 'axios';
import { API_CONFIG } from '../components/config/api';
import { getMembershipType, getDestinyMembershipId } from './authService';
import { getMockCharacters } from './apiMockService';

export interface Character {
  characterId: string;
  classType: number;
  className: string;
  race: string;
  gender: string;
  light: number;
  emblemPath: string;
  emblemBackgroundPath: string;
  lastPlayed: string;
  minutesPlayedTotal: number;
  level: number;
}

/**
 * Logs the auth state (for debugging)
 */
const logAuthState = () => {
  try {
    const authData = localStorage.getItem('destiny_auth');
    if (authData) {
      const parsedData = JSON.parse(authData);
      console.log('Auth state found in localStorage:', {
        isAuthenticated: parsedData.isAuthenticated,
        membershipType: parsedData.membershipType,
        destinyMembershipId: parsedData.destinyMembershipId,
        tokenExpires: parsedData.expires ? new Date(parsedData.expires).toISOString() : 'none',
        hasAccessToken: Boolean(parsedData.accessToken),
        hasRefreshToken: Boolean(parsedData.refreshToken),
      });
    } else {
      console.log('No auth state found in localStorage');
    }
  } catch (e) {
    console.error('Error logging auth state:', e);
  }
};

/**
 * Maps Bungie API class type to class name
 */
const getClassName = (classType: number): string => {
  switch (classType) {
    case 0:
      return 'Titan';
    case 1:
      return 'Hunter';
    case 2:
      return 'Warlock';
    default:
      return 'Unknown';
  }
};

/**
 * Maps Bungie API race type to race name
 */
const getRaceName = (raceType: number): string => {
  switch (raceType) {
    case 0:
      return 'Human';
    case 1:
      return 'Awoken';
    case 2:
      return 'Exo';
    default:
      return 'Unknown';
  }
};

/**
 * Maps Bungie API gender type to gender name
 */
const getGenderName = (genderType: number): string => {
  switch (genderType) {
    case 0:
      return 'Male';
    case 1:
      return 'Female';
    default:
      return 'Unknown';
  }
};

/**
 * Fetches the user's characters from the Bungie API
 */
export const getUserCharacters = async (): Promise<Character[]> => {
  try {
    // Log auth state for debugging
    logAuthState();
    
    // Get membership information from auth service
    const membershipType = getMembershipType();
    const destinyMembershipId = getDestinyMembershipId();
    
    if (!membershipType || !destinyMembershipId) {
      console.log('Membership information not available:', { 
        membershipType, 
        destinyMembershipId 
      });
      
      // In development or if no membership info is available, return dummy data
      return getDummyCharacters();
    }
    
    // Build the API URL
    const endpoint = API_CONFIG.ENDPOINTS.BUNGIE.DESTINY_CHARACTERS
      .replace('{membershipType}', membershipType.toString())
      .replace('{destinyMembershipId}', destinyMembershipId);
    
    console.log('Fetching characters from:', `${API_CONFIG.BUNGIE_API_BASE_URL}${endpoint}`);
    
    const response = await axios.get(`${API_CONFIG.BUNGIE_API_BASE_URL}${endpoint}`, {
      headers: {
        'X-API-Key': API_CONFIG.BUNGIE_API_KEY
      }
    });
    
    if (!response.data || !response.data.Response || !response.data.Response.characters || !response.data.Response.characters.data) {
      console.error('Invalid API response structure:', response.data);
      throw new Error('No character data found in response');
    }
    
    const charactersData = response.data.Response.characters.data;
    const characters: Character[] = [];
    
    // Process each character from the response
    for (const characterId in charactersData) {
      const char = charactersData[characterId];
      characters.push({
        characterId,
        classType: char.classType,
        className: getClassName(char.classType),
        race: getRaceName(char.raceType),
        gender: getGenderName(char.genderType),
        light: char.light,
        emblemPath: `https://www.bungie.net${char.emblemPath}`,
        emblemBackgroundPath: `https://www.bungie.net${char.emblemBackgroundPath}`,
        lastPlayed: char.dateLastPlayed,
        minutesPlayedTotal: char.minutesPlayedTotal,
        level: char.levelProgression.level
      });
    }
    
    // Sort characters by last played
    return characters.sort((a, b) => 
      new Date(b.lastPlayed).getTime() - new Date(a.lastPlayed).getTime()
    );
  } catch (error) {
    console.error('Error fetching user characters:', error);
    
    // Always return dummy data when there's an error
    return getDummyCharacters();
  }
};

/**
 * Returns dummy character data for development
 */
const getDummyCharacters = (): Character[] => {
  // Check the auth state in localStorage directly
  console.log('Getting dummy characters');
  const storedAuth = localStorage.getItem('destiny_auth');
  if (storedAuth) {
    console.log('Found stored auth, checking for necessary fields:', JSON.parse(storedAuth));
  } else {
    console.log('No stored auth found in localStorage');
  }
  
  // Use mock service to get better dummy data
  return getMockCharacters();
};